// src/pages/sqf/SqfTemplateAuditPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import companyService from '../../services/companyService';
import { useLanguage, t } from '../../context/LanguageContext';

const CATEGORY_LABELS = {
  SOP: 'SOP', SSOP: 'SSOP', SPC: 'SPC', SCPC: 'SCPC', CCP: 'CCP', OTHER: 'Other',
};

const displayName = (u) =>
  u ? ((u.firstName || u.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : u.username) : '—';

export default function SqfTemplateAuditPage() {
  const { id } = useParams();
  const { lang } = useLanguage();
  const [template, setTemplate] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const FIELD_TYPE_LABELS = {
    number:      t('Number', 'Número', lang),
    pass_fail:   t('Pass / Fail', 'Pasa / Falla', lang),
    yes_no:      t('Yes / No', 'Sí / No', lang),
    text:        t('Text', 'Texto', lang),
    select:      t('Dropdown', 'Lista desplegable', lang),
    multi_check: t('Multi-Check', 'Selección múltiple', lang),
    time:        t('Time', 'Hora', lang),
    photo:       t('Photo', 'Foto', lang),
    signature:   t('Signature', 'Firma', lang),
  };

  useEffect(() => {
    Promise.all([
      sqfService.getTemplate(id),
      companyService.getCompanies().catch(() => null),
    ]).then(([tmpl, co]) => {
      setTemplate(tmpl);
      if (co) setCompany(Array.isArray(co) ? co[0] : co);
    }).catch(() => setError(t('Failed to load audit log.', 'Error al cargar el registro de auditoría.', lang)))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return (
    <div className="container mt-4 text-center py-5 text-muted">
      <div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}
    </div>
  );
  if (error || !template) return (
    <div className="container mt-4"><div className="alert alert-danger">{error || t('Template not found.', 'Plantilla no encontrada.', lang)}</div></div>
  );

  const publishedVersions = (template.Versions || [])
    .filter(v => v.status === 'published' || v.status === 'archived')
    .sort((a, b) => b.versionNumber - a.versionNumber);

  const activeVersion = (template.Versions || []).find(v => v.status === 'published');
  const activeSchema = activeVersion?.schemaJson || { sections: [] };

  const companyAddr = [
    company?.addressLine1,
    company?.addressLine2,
    [company?.city, company?.stateProvince].filter(Boolean).join(', '),
  ].filter(Boolean).join(' · ');

  return (
    <div className="container mt-4 pb-5" id="audit-print-root">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 10mm 8mm; }
          .container { max-width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }
          #audit-table { width: 100%; border-collapse: collapse; }
          #audit-table thead th {
            padding: 6px 0 6px;
            border-bottom: 2px solid #000;
            font-weight: normal;
          }
          #audit-table tbody td { vertical-align: top; padding-top: 4mm; }
          .audit-section { page-break-inside: avoid; }
          body { font-size: 11pt; }
        }
        @media screen {
          #audit-table { display: block; }
          #audit-table > thead { display: none; }
          #audit-table > tbody { display: block; }
          #audit-table > tbody > tr { display: block; }
          #audit-table > tbody > tr > td { display: block; padding: 0; border: none; }
        }
      `}</style>

      {/* ── Print table with repeating header ─────────────────────────────────── */}
      <table id="audit-table">
        <thead>
          <tr>
            <th>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, fontSize: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{template.name}</div>
                  <div>{t('Revision Audit Log', 'Registro de Auditoría de Revisiones', lang)}</div>
                  {activeVersion && (
                    <div>
                      Rev. {activeVersion.versionNumber}
                      {activeVersion.revisionDate && (
                        <span> — {new Date(activeVersion.revisionDate + 'T00:00:00').toLocaleDateString()}</span>
                      )}
                    </div>
                  )}
                  {template.sqfReference && <div>{template.sqfReference}</div>}
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  {company?.logoUrl && (
                    <img src={company.logoUrl} alt="Logo" style={{ maxHeight: 48, maxWidth: 120, objectFit: 'contain' }} />
                  )}
                </div>
                <div style={{ flex: 1, textAlign: 'right', fontSize: 10 }}>
                  <div style={{ fontWeight: 700 }}>{company?.name || ''}</div>
                  {companyAddr && <div>{companyAddr}</div>}
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>

              {/* ── Screen-only nav ──────────────────────────────────────────────── */}
              <div className="no-print d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                  <Link to="/sqf/templates" className="text-muted" style={{ fontSize: 12 }}>← {t('Templates', 'Plantillas', lang)}</Link>
                  <h4 className="mb-0 mt-1">{template.name} — {t('Revision Audit Log', 'Registro de Auditoría de Revisiones', lang)}</h4>
                </div>
                <div className="d-flex gap-2">
                  <Link to={`/sqf/templates/${id}/edit`} className="btn btn-outline-secondary btn-sm">{t('Edit Template', 'Editar Plantilla', lang)}</Link>
                  <button className="btn btn-primary btn-sm" onClick={() => window.print()}>
                    🖨 {t('Print / Save as PDF', 'Imprimir / Guardar como PDF', lang)}
                  </button>
                </div>
              </div>

              {/* ── Template info ──────────────────────────────────────────────────── */}
              <div className="card mb-4 audit-section">
                <div className="card-body py-3">
                  <div className="row g-2" style={{ fontSize: 13 }}>
                    <div className="col-sm-4">
                      <span className="text-muted">{t('Category', 'Categoría', lang)}: </span>
                      <strong>{CATEGORY_LABELS[template.category] || template.category || '—'}</strong>
                    </div>
                    <div className="col-sm-4">
                      <span className="text-muted">{t('Current Revision', 'Revisión Actual', lang)}: </span>
                      <strong>{activeVersion ? `Rev. ${activeVersion.versionNumber}` : t('Not published', 'No publicado', lang)}</strong>
                    </div>
                    <div className="col-sm-4">
                      <span className="text-muted">{t('Revision Date', 'Fecha de Revisión', lang)}: </span>
                      <strong>
                        {activeVersion?.revisionDate
                          ? new Date(activeVersion.revisionDate + 'T00:00:00').toLocaleDateString()
                          : '—'}
                      </strong>
                    </div>
                    {template.sqfReference && (
                      <div className="col-sm-12">
                        <span className="text-muted">{t('SQF Reference', 'Referencia SQF', lang)}: </span>
                        <strong>{template.sqfReference}</strong>
                      </div>
                    )}
                    {template.description && (
                      <div className="col-sm-12">
                        <span className="text-muted">{t('Description', 'Descripción', lang)}: </span>
                        {template.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Revision history table ─────────────────────────────────────────── */}
              <h6 className="text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: 1, color: '#6c757d' }}>
                {t('Revision History', 'Historial de Revisiones', lang)}
              </h6>

              {publishedVersions.length === 0 ? (
                <div className="text-muted py-3" style={{ fontSize: 13 }}>{t('No published revisions yet.', 'Aún no hay revisiones publicadas.', lang)}</div>
              ) : (
                <table className="table table-sm table-bordered mb-4 audit-section" style={{ fontSize: 12 }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 70 }}>Rev. #</th>
                      <th style={{ width: 110 }}>{t('Date', 'Fecha', lang)}</th>
                      <th style={{ width: 90 }}>{t('Status', 'Estado', lang)}</th>
                      <th>{t('Change Summary', 'Resumen de Cambios', lang)}</th>
                      <th style={{ width: 130 }}>{t('Published By', 'Publicado por', lang)}</th>
                      <th style={{ width: 120 }}>{t('Published At', 'Publicado el', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishedVersions.map(v => (
                      <tr key={v.id}>
                        <td className="fw-semibold">Rev. {v.versionNumber}</td>
                        <td>
                          {v.revisionDate
                            ? new Date(v.revisionDate + 'T00:00:00').toLocaleDateString()
                            : '—'}
                        </td>
                        <td>
                          {v.status === 'published'
                            ? <span className="badge bg-success">{t('Active', 'Activo', lang)}</span>
                            : <span className="badge bg-secondary">{t('Archived', 'Archivado', lang)}</span>}
                        </td>
                        <td style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{v.notes || '—'}</td>
                        <td>{displayName(v.PublishedBy)}</td>
                        <td>{v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* ── Current checklist fields (active version snapshot) ─────────────── */}
              {activeVersion && (
                <>
                  <h6 className="text-uppercase mb-2 mt-4" style={{ fontSize: 11, letterSpacing: 1, color: '#6c757d' }}>
                    {t('Current Checklist Fields', 'Campos Actuales de la Lista de Verificación', lang)} — Rev. {activeVersion.versionNumber}
                  </h6>

                  {(activeSchema.sections || []).map((section, sIdx) => (
                    <div key={section.id || sIdx} className="mb-3 audit-section">
                      {section.title && (
                        <div className="fw-semibold mb-1" style={{ fontSize: 13, borderBottom: '1px solid #dee2e6', paddingBottom: 4 }}>
                          {section.title}
                        </div>
                      )}
                      <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>{t('Field Label', 'Etiqueta del Campo', lang)}</th>
                            <th style={{ width: 110 }}>{t('Type', 'Tipo', lang)}</th>
                            <th style={{ width: 80 }}>{t('Required', 'Requerido', lang)}</th>
                            <th>{t('Validation / Options', 'Validación / Opciones', lang)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(section.fields || []).map((field, fIdx) => {
                            const v = field.validation || {};
                            let validationText = '';
                            if (field.type === 'number') {
                              const parts = [];
                              if (v.min !== undefined) parts.push(`${t('Min', 'Mín', lang)}: ${v.min}${v.deviationIfBelow ? ` (${t('dev', 'desv', lang)})` : ''}`);
                              if (v.max !== undefined) parts.push(`${t('Max', 'Máx', lang)}: ${v.max}${v.deviationIfAbove ? ` (${t('dev', 'desv', lang)})` : ''}`);
                              if (field.unit) parts.push(`${t('Unit', 'Unidad', lang)}: ${field.unit}`);
                              validationText = parts.join(' · ');
                            } else if (field.type === 'yes_no') {
                              const parts = [];
                              if (v.deviationIfYes) parts.push(t('Dev if Yes', 'Desv. si Sí', lang));
                              if (v.deviationIfNo) parts.push(t('Dev if No', 'Desv. si No', lang));
                              validationText = parts.join(', ');
                            } else if (['select', 'multi_check'].includes(field.type)) {
                              validationText = (field.options || []).join(', ');
                            }
                            return (
                              <tr key={field.id || fIdx}>
                                <td className="text-muted">{fIdx + 1}</td>
                                <td>
                                  {field.label}
                                  {field.helpText && <div className="text-muted" style={{ fontSize: 10 }}>{field.helpText}</div>}
                                </td>
                                <td>{FIELD_TYPE_LABELS[field.type] || field.type}</td>
                                <td>{field.required ? '✓' : ''}</td>
                                <td style={{ fontSize: 11, color: '#6c757d' }}>{validationText || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  <div className="mt-4 pt-3 audit-section" style={{ borderTop: '1px solid #dee2e6', fontSize: 11, color: '#6c757d' }}>
                    <div className="row g-2">
                      <div className="col-sm-4">
                        {t('Document', 'Documento', lang)}: {template.name}
                      </div>
                      <div className="col-sm-4 text-center">
                        Rev. {activeVersion.versionNumber}
                        {activeVersion.revisionDate && (
                          <> — {new Date(activeVersion.revisionDate + 'T00:00:00').toLocaleDateString()}</>
                        )}
                      </div>
                      <div className="col-sm-4 text-end">
                        {t('Printed', 'Impreso', lang)}: {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </>
              )}

            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
